import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Typography, Button, Chip, CircularProgress, alpha, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TextField, MenuItem, IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import PageLoader from "../../Components/Loader/PageLoader";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import logoBlack from "../../Images/logo_main_black.webp";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();
const ACCENT = "#00838f";

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  draft: { label: "Draft", color: "#64748b", bg: "#f1f5f9" },
  sent: { label: "Sent", color: "#3b82f6", bg: "#eff6ff" },
  confirmed: { label: "Confirmed", color: "#7c3aed", bg: "#f5f3ff" },
  partially_received: { label: "Partial", color: "#ea580c", bg: "#fff7ed" },
  received: { label: "Received", color: "#059669", bg: "#ecfdf5" },
  cancelled: { label: "Cancelled", color: "#dc2626", bg: "#fef2f2" },
};

const PurchaseOrders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  const getHeaders = () => ({
    "Content-Type": "application/json", Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  });

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter !== "all") params.set("status", statusFilter);
    fetch(`${API_URL}/procurement/purchase-orders?${params}`, { headers: getHeaders() })
      .then(r => r.json())
      .then(json => { if (json.status) setOrders(json.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [statusFilter]);

  const stats = useMemo(() => ({
    total: orders.length,
    draft: orders.filter(o => o.status === "draft").length,
    sent: orders.filter(o => o.status === "sent" || o.status === "confirmed").length,
    received: orders.filter(o => o.status === "received" || o.status === "partially_received").length,
    totalValue: orders.reduce((a, o) => a + Number(o.total_amount || 0), 0),
  }), [orders]);

  const buildReportHtml = (rows: any[], title: string): string => {
    const logoSrc = (logoBlack as string);
    const logoUrl = logoSrc.startsWith("http") || logoSrc.startsWith("data:")
      ? logoSrc
      : `${window.location.origin}${logoSrc}`;
    const reportDate = dayjs().format("MMMM DD, YYYY");
    const preparedBy = localStorage.getItem("user_email") || localStorage.getItem("email") || "—";
    const filterLabel = statusFilter === "all"
      ? "All Purchase Orders"
      : (STATUS_CONFIG[statusFilter]?.label || statusFilter);

    const totalAmount = rows.reduce((s, o) => s + Number(o.total_amount || 0), 0);
    const draftCount = rows.filter(o => o.status === "draft").length;
    const receivedCount = rows.filter(o => o.status === "received" || o.status === "partially_received").length;

    const statusPill = (s: string) => {
      const k = (s || "").toLowerCase();
      const cls = ["received","partially_received"].includes(k) ? "ok"
        : ["sent","confirmed"].includes(k) ? "info"
        : k === "cancelled" ? "danger"
        : "neutral";
      const label = STATUS_CONFIG[k]?.label || (s ? s.charAt(0).toUpperCase() + s.slice(1) : "—");
      return `<span class="status ${cls}">${label}</span>`;
    };

    const rowsHtml = rows.map((o: any, i: number) => `
      <tr>
        <td class="num">${i + 1}</td>
        <td class="po"><div class="po-main">${o.po_number || "—"}</div></td>
        <td>${o.order_date ? dayjs(o.order_date).format("MMM D, YYYY") : "—"}</td>
        <td class="supplier">${o.supplier_name || "—"}</td>
        <td class="right">${o.item_count ?? 0}</td>
        <td><span class="src ${o.source === "auto_demand" ? "auto" : "manual"}">${o.source === "auto_demand" ? "Auto" : "Manual"}</span></td>
        <td class="right amt">$${Number(o.total_amount || 0).toFixed(2)}</td>
        <td>${statusPill(o.status)}</td>
      </tr>
    `).join("");

    return `<!DOCTYPE html><html><head><title>CaCart ${title}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'Segoe UI',Arial,sans-serif;color:#1f2937;background:#fff;padding:36px 40px}
  .report{max-width:1180px;margin:0 auto}
  .report-header{display:flex;justify-content:space-between;align-items:flex-start;gap:24px;margin-bottom:18px}
  .header-left .logo{height:46px;width:auto;display:block;margin-bottom:10px}
  .header-left .meta{font-size:12px;color:#475569;line-height:1.7}
  .header-right{text-align:right}
  .header-right .title{font-size:26px;font-weight:800;color:#0B1F3A;letter-spacing:.6px;font-family:Georgia,'Times New Roman',serif}
  .header-right .meta{font-size:12px;color:#475569;line-height:1.7;margin-top:4px}
  .filter-strip{background:#eff4fb;border-top:1px solid #d6e1ef;border-bottom:1px solid #d6e1ef;padding:10px 14px;font-size:12px;color:#0B1F3A;margin-bottom:18px}
  .filter-strip strong{font-weight:700;margin-right:6px}
  .summary{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:18px}
  .sum-card{border:1px solid;padding:18px 20px;border-radius:4px;text-align:center}
  .sum-card .label{font-size:10.5px;font-weight:700;letter-spacing:1.2px;color:#64748b;margin-bottom:8px}
  .sum-card .value{font-size:22px;font-weight:800;font-family:Georgia,'Times New Roman',serif}
  .sum-card.total{background:#f3f6fb;border-color:#dde5f0}.sum-card.total .value{color:#0B1F3A}
  .sum-card.amount{background:#eef4fc;border-color:#cfdcef}.sum-card.amount .value{color:#1d4ed8}
  .sum-card.draft{background:#f1f5f9;border-color:#cbd5e1}.sum-card.draft .value{color:#475569}
  .sum-card.received{background:#eafaf0;border-color:#c5ecd2}.sum-card.received .value{color:#15803d}
  table{width:100%;border-collapse:collapse;font-size:12.5px}
  thead th{background:#0B1F3A;color:#fff;font-size:10.5px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;padding:12px 12px;text-align:left;border-bottom:1px solid #0B1F3A}
  thead th.right{text-align:right}
  tbody tr:nth-child(even) td{background:#f9fafb}
  tbody td{padding:14px 12px;border-bottom:1px solid #e5e7eb;vertical-align:middle}
  tbody td.num{color:#94a3b8;font-size:11px;width:36px}
  tbody td.right{text-align:right}
  .po-main{font-weight:700;color:#0B1F3A;font-family:'Courier New',monospace}
  tbody td.supplier{font-weight:600;color:#1f2937}
  tbody td.amt{font-weight:700;color:#15803d}
  .src{display:inline-block;padding:2px 8px;border-radius:4px;font-size:10.5px;font-weight:700}
  .src.auto{background:#f5f3ff;color:#7c3aed}
  .src.manual{background:#f1f5f9;color:#475569}
  .status{display:inline-block;padding:3px 10px;border-radius:4px;font-size:11px;font-weight:700}
  .status.ok{color:#15803d}
  .status.info{color:#1d4ed8}
  .status.neutral{color:#475569}
  .status.danger{color:#b91c1c}
  tfoot td{background:#0B1F3A;color:#fff;font-size:12.5px;font-weight:700;padding:14px 12px;letter-spacing:.4px}
  tfoot td.right{text-align:right}
  .divider{height:1px;background:#e5e7eb;margin:28px 0 14px 0}
  .footer{text-align:center;font-size:11px;color:#64748b;padding-top:6px}
  .footer .brand{color:#0B1F3A;font-weight:700}
  @media print{body{padding:18px}}
</style>
</head><body>
<div class="report">
  <div class="report-header">
    <div class="header-left">
      <img src="${logoUrl}" alt="CaCart" class="logo" />
      <div class="meta">
        CaCart — Procurement & Inventory System<br/>
        support@cacart.com<br/>
      </div>
    </div>
    <div class="header-right">
      <div class="title">${title.toUpperCase()}</div>
      <div class="meta">
        Date: ${reportDate}<br/>
        Prepared By: ${preparedBy}
      </div>
    </div>
  </div>
  <div class="filter-strip"><strong>Filter:</strong>${filterLabel}</div>
  <div class="summary">
    <div class="sum-card total"><div class="label">TOTAL POs</div><div class="value">${rows.length}</div></div>
    <div class="sum-card amount"><div class="label">TOTAL AMOUNT</div><div class="value">$${totalAmount.toFixed(2)}</div></div>
    <div class="sum-card draft"><div class="label">TOTAL DRAFT</div><div class="value">${draftCount}</div></div>
    <div class="sum-card received"><div class="label">TOTAL RECEIVED</div><div class="value">${receivedCount}</div></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>PO Number</th>
        <th>Date</th>
        <th>Supplier</th>
        <th class="right">Items</th>
        <th>Source</th>
        <th class="right">Amount</th>
        <th>Status</th>
      </tr>
    </thead>
    <tbody>${rowsHtml || `<tr><td colspan="8" style="text-align:center;padding:24px;color:#94a3b8">No purchase orders</td></tr>`}</tbody>
    <tfoot>
      <tr>
        <td colspan="6">TOTALS &nbsp;|&nbsp; ${rows.length} purchase order${rows.length === 1 ? "" : "s"}</td>
        <td class="right">$${totalAmount.toFixed(2)}</td>
        <td></td>
      </tr>
    </tfoot>
  </table>
  <div class="divider"></div>
  <div class="footer">
    <div><span class="brand">CaCart</span> — Procurement & Inventory System</div>
    <div>Generated via CaCart &nbsp;•&nbsp; © ${dayjs().format("YYYY")}</div>
  </div>
</div>
<script>window.onload=function(){window.print()}</script>
</body></html>`;
  };

  const openReport = (rows: any[], title: string) => {
    const w = window.open("", "_blank", "width=1200,height=900");
    if (!w) return;
    w.document.write(buildReportHtml(rows, title));
    w.document.close();
  };

  const handlePrint = () => {
    openReport(orders, "Purchase Order Report");
  };

  const handlePrintPo = (po: any) => {
    openReport([po], "Purchase Order Report");
  };

  const handleEditPo = (po: any) => {
    const editPath = po.table_source === "bill"
      ? `/procurement/purchase-orders/bill-${po.id}/edit`
      : `/procurement/purchase-orders/edit/${po.id}`;
    navigate(editPath);
  };

  const handleExport = () => {
    const rows = orders.map(o => ({
      "PO #": o.po_number, Supplier: o.supplier_name, Status: o.status,
      "Total": Number(o.total_amount).toFixed(2), Items: o.item_count,
      Source: o.source, Date: o.order_date, Created: dayjs(o.created_at).format("MMM D, YYYY"),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0] || {}).map(k => ({ wch: Math.max(k.length, 12) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Purchase Orders");
    XLSX.writeFile(wb, `PurchaseOrders_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  if (loading) return <PageLoader />;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, bgcolor: "#F6F8FA", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .no-print, .sidebar, header, nav, .MuiDrawer-root { display: none !important; }
          body { background: #fff !important; }
          .MuiCard-root, .MuiPaper-root { box-shadow: none !important; }
          table { font-size: 12px; border-collapse: collapse; width: 100%; }
          th, td { padding: 8px !important; border: 1px solid #ddd !important; }
        }
      `}</style>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: "#EEF2F7",
          }}>
            <AddShoppingCartIcon sx={{ fontSize: 22, color: "#0B1E2D" }} />
          </Box>
          <Box>
            <Typography fontSize={22} fontWeight={700} color="#0B1E2D" letterSpacing="-0.02em">Purchase Orders</Typography>
            <Typography fontSize={12} color="#64748B">{stats.total} orders · ${stats.totalValue.toLocaleString("en-CA", { minimumFractionDigits: 2 })}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField className="no-print" select size="small" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            sx={{ minWidth: 140, "& .MuiOutlinedInput-root": { borderRadius: 2.5, fontSize: 12, "& fieldset": { borderColor: "#e2e8f0" } } }}>
            <MenuItem value="all">All Status</MenuItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </TextField>
          <Button className="no-print" size="small" variant="outlined" startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />} onClick={handleExport}
            sx={{ borderRadius: "8px", textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#E5E7EB", color: "#0B1E2D", "&:hover": { borderColor: "#0B1E2D", bgcolor: "#F6F8FA" } }}>Excel</Button>
          <Button className="no-print" size="small" variant="outlined" startIcon={<PrintIcon sx={{ fontSize: 16 }} />} onClick={handlePrint}
            sx={{ borderRadius: "8px", textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#E5E7EB", color: "#0B1E2D", "&:hover": { borderColor: "#0B1E2D", bgcolor: "#F6F8FA" } }}>Print</Button>
          <Button className="no-print" size="small" variant="contained" disableElevation startIcon={<AddShoppingCartIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate("/bill/create")}
            sx={{ borderRadius: "8px", textTransform: "none", fontSize: 12, fontWeight: 600, bgcolor: "#0B1E2D", color: "#fff", "&:hover": { bgcolor: "#0F2A4A" } }}>
            New Purchase Order
          </Button>
        </Box>
      </Box>

      {/* KPIs */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        {[
          { label: "Total POs", value: stats.total, color: "#2563EB", bg: "#EFF6FF" },
          { label: "Draft", value: stats.draft, color: "#64748B", bg: "#F1F5F9" },
          { label: "In Progress", value: stats.sent, color: "#7C3AED", bg: "#F5F3FF" },
          { label: "Received", value: stats.received, color: "#0E9F6E", bg: "#ECFDF5" },
        ].map((kpi, i) => (
          <Box key={i} sx={{
            flex: "1 1 0", minWidth: 140, p: 2.25, borderRadius: "12px", bgcolor: "#fff",
            border: "1px solid #E5E7EB",
            transition: "border-color 0.18s ease",
            "&:hover": { borderColor: "#cbd5e1" },
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: kpi.color }} />
              <Typography fontSize={10} fontWeight={600} color="#94A3B8" textTransform="uppercase" letterSpacing={0.5}>{kpi.label}</Typography>
            </Box>
            <Typography fontSize={24} fontWeight={700} color="#0B1E2D" letterSpacing="-0.02em">{kpi.value}</Typography>
          </Box>
        ))}
      </Box>

      {/* Table */}
      <Box sx={{ bgcolor: "#fff", borderRadius: "10px", border: "1px solid #E5E7EB", overflow: "hidden" }}>
        <TableContainer>
          <Table sx={{
            "& .MuiTableHead-root .MuiTableRow-root": { backgroundColor: "#0B1E2D" },
            "& .MuiTableHead-root .MuiTableCell-root": {
              backgroundColor: "#0B1E2D",
              backgroundImage: "none",
              color: "#fff",
              fontSize: 10.5,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              verticalAlign: "middle",
              borderBottom: "none",
              boxShadow: "none",
              py: 1.5,
            },
          }}>
            <TableHead>
              <TableRow>
                {["PO #", "Supplier", "Items", "Total", "Source", "Date", "Status", "Actions"].map(h => (
                  <TableCell key={h} className={h === "Actions" ? "no-print" : undefined}
                    sx={{ minWidth: h === "Actions" ? 140 : undefined }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow><TableCell colSpan={8} sx={{ textAlign: "center", py: 8 }}>
                  <AddShoppingCartIcon sx={{ fontSize: 40, color: "#e2e8f0", mb: 1 }} />
                  <Typography fontSize={14} color="#94a3b8">No purchase orders yet</Typography>
                </TableCell></TableRow>
              ) : orders.map((po: any) => {
                const sc = STATUS_CONFIG[po.status] || STATUS_CONFIG.draft;
                const detailPath = po.table_source === "bill"
                  ? `/procurement/purchase-orders/bill-${po.id}`
                  : `/procurement/purchase-orders/${po.id}`;
                return (
                  <TableRow key={`${po.table_source}-${po.id}`} hover sx={{ cursor: "pointer", "&:hover": { bgcolor: "#F6F8FA" }, "& td": { borderBottom: "1px solid #F1F5F9" } }}
                    onClick={() => navigate(detailPath)}>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography fontSize={13} fontWeight={700} color={ACCENT} sx={{ fontFamily: "monospace" }}>{po.po_number}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}><Typography fontSize={12} fontWeight={500}>{po.supplier_name || "—"}</Typography></TableCell>
                    <TableCell sx={{ py: 1.5 }}><Typography fontSize={12}>{po.item_count}</Typography></TableCell>
                    <TableCell sx={{ py: 1.5 }}><Typography fontSize={13} fontWeight={700} color="#059669">${Number(po.total_amount || 0).toFixed(2)}</Typography></TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Chip label={po.source === "auto_demand" ? "Auto" : "Manual"} size="small"
                        sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: po.source === "auto_demand" ? "#f5f3ff" : "#f1f5f9", color: po.source === "auto_demand" ? "#7c3aed" : "#64748b" }} />
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}><Typography fontSize={11} color="#64748b">{dayjs(po.order_date).format("MMM D, YYYY")}</Typography></TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Chip label={sc.label} size="small" sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: sc.bg, color: sc.color, border: `1px solid ${alpha(sc.color, 0.15)}` }} />
                    </TableCell>
                    <TableCell sx={{ py: 1.5, verticalAlign: "middle" }} className="no-print">
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(detailPath); }}
                            sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "#f8fafc", color: "#0B1F3A", border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#e2e8f0" } }}>
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditPo(po); }}
                            sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "#f8fafc", color: "#0B1F3A", border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#e2e8f0" } }}>
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Print">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handlePrintPo(po); }}
                            sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "#f8fafc", color: "#0B1F3A", border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#e2e8f0" } }}>
                            <PrintIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default PurchaseOrders;
